import React, { useRef, useState, useEffect } from 'react'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { Edit, ManageSearchOutlined, TextSnippetOutlined } from '@mui/icons-material'
import AddElement from '../../generic/smartAddElement/AddElement'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import Tool from './Tool'
import { apiDelete, apiPost, apiGet } from '../../generic/Api_Functions'
import { IconButton, TextField } from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'
import AlertUI from '../../generic/AlertUI'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../generic/systemValues/systemValueLabel'
import Document from '../document/Document'
import { CheckCorrectTimeFormat } from '../../generic/DateTimeFunctions'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import useRepositorySelection from '../../generic/hooks/useRepositorySelection'
import { parentPreselectedRecordFrom, childPreselectedRecordFrom, childPreselectedRecordForRow } from '../../generic/utilities/repositorySelectionMapping'

const SEEDED_TOOL_REFERENCES = ['UNKNOWN_MILLING', 'UNKNOWN_TURNING']

const ToolReference = () => {
  const navigate = useNavigate()
  const [AlertElement, showAlert] = AlertUI()
  const state = useLocation()
  const show = useRef(showAlert)
  const [hideColumn, setHideColumn] = useState(false)
  const [editingRow, setEditingRow] = useState(null)
  const [toolDialogOpen, setToolDialogOpen] = useState(false)
  const [rowTool, setRowTool] = useState({})
  const { t_ } = useSmartTranslation()
  const [showDocument, setShowDocument] = useState(false)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const isUserAuthorized = useAuthorization()
  // Historia 170 (J4b, S104/S105): TOOL_REFERENCE -> (child) TOOL. `ToolReferenceSettings.goBack`/
  // `ToolInstanceSettings` (existing callers, S105) navigate here with the tool ROW ITSELF as
  // `state` (`state.state instanceof Object` below, RN34c/G1-5) — `repositorySelection` must be
  // recognized BEFORE that generic branch, or an empty `{}` (left behind once
  // `useRepositorySelection` strips it from history) would otherwise be misread as "a row".
  const repositorySelection = useRepositorySelection()
  const handleToolReferencePreselectionResolved = (row) => {
    if (row && childPreselectedRecordFrom(repositorySelection)) {
      setRowTool(row)
      setToolDialogOpen(true)
    }
  }

  const onEditIndexChanged = (editIndex, row) => {
    setHideColumn(false)
    if (editIndex === -1) {
      // Creating new element
      setHideColumn(true)
    }

    if (editIndex >= 0) {
      // Editing element. Row will have data
      setEditingRow(row)
      setHideColumn(false)
    }
  }

  const checkTotalLifeAndLifeWarningformat = (row) => {
    const { unit, totalLife, lifeWarning } = row
    let errorMsg = ''
    const checkAndFormat = (value) => {
      if (value !== null && value !== '') {
        return CheckCorrectTimeFormat(value, t_)
      }
      return ''
    }
    if (unit && unit.code === 'min') {
      errorMsg = checkAndFormat(totalLife) || checkAndFormat(lifeWarning)
    }
    return errorMsg
  }

  useEffect(() => {
    // S104 — recognized BEFORE the generic "any object in state is a row" branch below (RN34c/G1-5).
    if (repositorySelection) return
    if (state.state !== null && state.state instanceof Object) {
      setRowTool(state.state)
      setToolDialogOpen(true)
    } else if (state.state === 'BACK') {
      show.current({
        severity: 'info'
      })
    } else if (state.state === 'EDIT') {
      show.current({
        title: t_('Tool parameters modified correctly.'),
        severity: 'success'
      })
    }
  }, [state, t_, repositorySelection])

  const columns = [{
    name: t_('Code'),
    field: 'code',
    charLimit: 60,
    mandatory: true,
    unique: true,
    readOnly: true
  },
  {
    name: t_('Name'),
    field: 'name',
    charLimit: 80,
    mandatory: true,
    // Only the two references Sun seeds (UNKNOWN_MILLING, UNKNOWN_TURNING) are factory text; the rest are the customer's.
    render: (row) => (SEEDED_TOOL_REFERENCES.includes(row.code) ? systemValueLabel(t_, 'tool_reference', row) : row.name),
    renderOnlyInReadMode: true
  },
  {
    name: t_('Description'),
    field: 'description',
    charLimit: 200
  },
  {
    name: t_('Price'),
    field: 'price',
    type: 'text',
    numeric: true,
    decimals: 2
  },
  {
    name: t_('Tool type'),
    field: 'toolType.name',
    type: 'select',
    edit: {
      source: async () => {
        return await apiGet('allToolTypesBasicData')
      },
      id: 'id',
      field: 'name'
    },
    mandatory: true
  },
  {
    name: 'D',
    field: 'parameterD',
    edit: false,
    render: (row) => {
      if (row.parameterList === undefined) {
        return null
      } else {
        const valueFound = row.parameterList.find((parameterValue) => parameterValue.name === 'Diameter')
        return (valueFound !== undefined ? valueFound.value : null)
      }
    }
  },
  {
    name: 'RC',
    field: 'parameterRC',
    edit: false,
    render: (row) => {
      if (row.parameterList === undefined) {
        return null
      } else {
        const valueFound = row.parameterList.find((parameterValue) => parameterValue.name === 'Corner Radius')
        return (valueFound !== undefined ? valueFound.value : null)
      }
    }
  },
  {
    name: 'TL',
    field: 'parameterTL',
    edit: false,
    render: (row) => {
      if (row.parameterList === undefined) {
        return null
      } else {
        const valueFound = row.parameterList.find((parameterValue) => parameterValue.name === 'Total Length')
        return (valueFound !== undefined ? valueFound.value : null)
      }
    }
  },
  {
    name: 'CL',
    field: 'parameterCL',
    charLimit: 200,
    edit: false,
    render: (row) => {
      if (row.parameterList === undefined) {
        return null
      } else {
        const valueFound = row.parameterList.find((parameterValue) => parameterValue.name === 'Cutting Length')
        return (valueFound !== undefined ? valueFound.value : null)
      }
    }
  },
  {
    name: 'Z',
    field: 'parameterZ',
    edit: false,
    render: (row) => {
      if (row.parameterList === undefined) {
        return null
      } else {
        const valueFound = row.parameterList.find((parameterValue) => parameterValue.name === 'Number of Flutes')
        return (valueFound !== undefined ? valueFound.value : null)
      }
    }
  },
  {
    name: t_('Total Life'),
    field: 'totalLife',
    condition: (row) => {
      return checkTotalLifeAndLifeWarningformat(row)
    }
  },
  {
    name: t_('Life warning'),
    field: 'lifeWarning',
    condition: (row) => {
      return checkTotalLifeAndLifeWarningformat(row)
    }
  },
  {
    name: t_('Tool Life Units'),
    field: 'unit.code',
    type: 'select',
    showWhenColumnVisible: ['totalLife', 'lifeWarning'],
    mandatory: (row) => {
      return row.lifeWarning || row.totalLife
    },
    edit: {
      source: async () => {
        const units = await apiGet('units')
        return units
      },
      id: 'id',
      field: 'code'
    },
    condition: (row) => {
      return checkTotalLifeAndLifeWarningformat(row)
    }
  },
  {
    name: t_('Holder'),
    field: 'defaultFixtureReference.code',
    type: 'select',
    edit: {
      source: async () => {
        if (editingRow !== null) { return await apiGet('fixtureReferencesByToolReference/' + editingRow.id) }
      },
      id: 'id',
      field: 'code'
    },
    hide: hideColumn
  },
  {
    name: t_('Material type'),
    field: 'materialType.name',
    type: 'select',
    mandatory: true,
    edit: {
      source: async () => {
        return await apiGet('materialTypes')
      },
      id: 'id',
      field: 'name'
    }
  },
  {
    name: t_('Quantity'),
    field: 'quantity',
    defaultNewValue: 0,
    mandatory: true,
    numeric: true,
    render: (row, extraFunctions) => {
      return (
        row !== undefined && row.id === undefined && row.quantity === 0
          ? <TextField
              value={quantityValue}
              onChange={(event) => {
                setQuantityValue(event.target.value)
              }}
              helperText={' '}
              disabled={!isUserAuthorized('ADD_TOOL')}
              type='number'
              inputProps={{ min: 0 }}
            />

          : (
            <div id={'quantity' + row.id} style={{ alignItems: 'center', display: 'flex' }}>
              {row.quantity}
              <AddElement
                endpoint='tools'
                parameter={row.id}
                referenceName={row.name}
                disable={editing || disableRowActions.current || !isUserAuthorized('ADD_TOOL')}
                onAlert={extraFunctions.handleShowAlert}
              />
            </div>))
    }
  }
  ]

  const handleDataVisibleInNextWindow = (row) => {
    delete row['smart-table-index']
    delete row.quantity
    row.active = true
    // row.userPermision = userPermission
    return row
  }

  const actions = {
    create: {
      fetchFunction: (row) => {
        row.quantity = quantityValue
        if (row.unit === null || row.unit === undefined || Object.keys(row.unit).length === 0) {
          row.unit = null
        }
        setQuantityValue(0)
        return apiPost('toolReferences', row).then((toolReference) => {
          createToolParameterValuesInheritToolReferenceParameterValues(toolReference)
          setRowTool(toolReference)
          navigate('/resources/toolManager/' + toolReference.id, { state: { row: handleDataVisibleInNextWindow(toolReference) } })
        })
      },
      disabled: !isUserAuthorized('ADD_TOOL_REFERENCE')
    },
    delete: {
      fetchFunction: (rows) => {
        if (rows.length === 1) {
          return apiDelete('toolReferences/' + rows[0].id, rows)
        } else {
          return apiDelete('toolReferences', rows)
        }
      },
      disabled: !isUserAuthorized('DELETE_TOOL_REFERENCE')
    },
    custom: [
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        onClick: (row) => {
          setRowTool(row)
          setShowDocument(true)
        },
        disabled: (row) => editing || !isUserAuthorized('DOCUMENTS_TOOL_REFERENCE')
      },
      {
        name: 'View Tools',
        render: (row) => {
          return (
            <IconButton>
              <ManageSearchOutlined />
            </IconButton>
          )
        },
        disabled: (row) => !!editing || !isUserAuthorized('TOOL_INSTANCES'),
        onClick: (row) => {
          setRowTool(row)
          setToolDialogOpen(true)
        }
      },
      {
        name: 'Edit',
        render: (row) => {
          return (
            <IconButton>
              <Edit />
            </IconButton>
          )
        },
        disabled: !isUserAuthorized('EDIT_TOOL_REFERENCE'),
        onClick: (row) => (
          navigate('/resources/toolManager/' + row.id, { state: { row: handleDataVisibleInNextWindow(row) } })
        )
      }
    ]
  }

  const createToolParameterValuesInheritToolReferenceParameterValues = (rowTool) => {
    apiPost('toolByToolReference', rowTool)
  }
  const [quantityValue, setQuantityValue] = useState(0)
  const [editing, setEditing] = useState(false)
  const disableRowActions = useRef(false)
  return (
    <div>
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={<Document reference={rowTool} resourceType='TOOL_REFERENCE' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />}
        setOpen={showDocument}
      />
      {AlertElement}
      <SmartDialog
        cancel={false} accept={false} close closeCallback={() => setToolDialogOpen(false)} setOpen={toolDialogOpen}
        renderComponent={<Tool row={rowTool} endpoint='tools' units={rowTool.unit ? rowTool.unit.code : ''} showQrAction preselectedRecord={childPreselectedRecordForRow(repositorySelection, rowTool.id)} />}
      />
      <SmartMateriaUITable
        tableTuneUniqueKey='toolReference'
        title={t_('Tools')}
        columns={columns}
        dataFetch='toolReferencesDto'
        dense
        actions={actions}
        preselectedRecord={parentPreselectedRecordFrom(repositorySelection)}
        onPreselectionResolved={handleToolReferencePreselectionResolved}
        multipleSelection
        editingRow={(row) => setEditing(row)}
        setDisableRowActions={(event) => disableRowActions.current === event}
        onEditIndexChanged={onEditIndexChanged}
        importFile={[{
          bean: {
            beanDtoName: 'ImportToolReferenceMill', beanName: 'ToolReference', beanDtoPackage: 'com.smartpm.sun.dto', beanPackage: 'com.smartpm.sun.domainobject', servicePackage: 'com.smartpm.sun.dbservices', serviceName: 'ImportToolReferenceMillService'
          },
          name: t_('Mill')
        },
        { bean: { beanDtoName: 'ImportToolReferenceTurn', beanName: 'ToolReference', beanDtoPackage: 'com.smartpm.sun.dto', beanPackage: 'com.smartpm.sun.domainobject', servicePackage: 'com.smartpm.sun.dbservices', serviceName: 'ImportToolReferenceTurnService' }, name: t_('Turn') }

        ]}
        importFileDisabled={!isUserAuthorized('IMPORT_FILE_TOOL_REFERENCE')}
      />
    </div>
  )
}

export default ToolReference
